import { v4 as uuidv4 } from 'uuid'
import { FloorTable, Room, TableShape } from '../types/floorPlan.model'
import {
  getTableFootprint,
  getTableOutline,
  outlinesOverlap,
  PlanArea
} from './seatLayout'

// Stable seed IDs so the persisted default rooms stay referentially consistent.
export const DEFAULT_ROOM_MAIN_ID = 'room-main'
export const DEFAULT_ROOM_PATIO_ID = 'room-patio'

export const DEFAULT_ROOMS: Room[] = [
  { id: DEFAULT_ROOM_MAIN_ID, name: 'Sala główna', order: 0 },
  { id: DEFAULT_ROOM_PATIO_ID, name: 'Patio', order: 1 }
]

export const CANVAS_WIDTH = 1200
export const CANVAS_HEIGHT = 700

export const MIN_TABLE_SIZE = 40

// Grid cell size in px. MUST stay in sync with the 40px background grid defined
// in FloorPlanCanvas.module.scss so snapping lands on the visible grid lines.
export const GRID_SIZE = 40

/** Snaps a single coordinate to the nearest grid line. */
export const snapToGrid = (value: number) =>
  Math.round(value / GRID_SIZE) * GRID_SIZE

// Zoom bounds for the whole planner (1 = 100%). The low minimum lets the
// "centre on tables" action zoom out far enough to find widely-spread tables.
export const ZOOM_MIN = 0.1
export const ZOOM_MAX = 2
export const ZOOM_STEP = 0.1

export const DEFAULT_DIMENSIONS: Record<
  TableShape,
  { width: number; height: number }
> = {
  round: { width: 90, height: 90 },
  square: { width: 90, height: 90 },
  rectangle: { width: 160, height: 90 }
}

/**
 * Returns dimensions for a shape change that preserve the current size where it
 * makes sense, but guarantee round/square stay square and rectangle stays
 * genuinely non-square.
 */
export function getDimensionsForShape(
  shape: TableShape,
  currentWidth: number,
  currentHeight: number
): { width: number; height: number } {
  if (shape === 'rectangle') {
    if (currentWidth === currentHeight) {
      // was square, give it a rectangular footprint
      return {
        width: Math.round(currentWidth * 1.7),
        height: currentHeight
      }
    }
    return { width: currentWidth, height: currentHeight }
  }

  // round / square must be square, collapse to a single side length
  const side = Math.min(currentWidth, currentHeight)
  return { width: side, height: side }
}

/**
 * Computes the next table number from the highest existing numeric label across
 * ALL rooms, so numbering is global, a new room continues where the previous
 * left off (room 1 has 1–10 → first table in room 2 is 11). Labels are plain
 * numbers (e.g. "7").
 */
export function getNextTableLabel(tables: FloorTable[]): string {
  const maxNumber = tables.reduce((max, table) => {
    const match = table.label.match(/(\d+)$/)
    const num = match ? parseInt(match[1], 10) : 0
    return Math.max(max, num)
  }, 0)

  return String(maxNumber + 1)
}

export function createTable(
  roomId: string,
  shape: TableShape,
  seatCount: number,
  label: string,
  position: { x: number; y: number }
): FloorTable {
  const { width, height } = DEFAULT_DIMENSIONS[shape]

  return {
    // Non-uuid prefix marks a client-created table the backend hasn't seen yet,
    // so its id is OMITTED on the first save (create) and only sent once the
    // backend has assigned a real uuid (mirrors how rooms use `room-<uuid>`).
    id: `table-${uuidv4()}`,
    roomId,
    label,
    shape,
    seatCount,
    width,
    height,
    x: position.x,
    y: position.y,
    rotation: 0
  }
}

/**
 * Finds a spot for a new table inside `area`, the part of the room on screen,
 * where it and its chairs cross no other table's: the first one in reading
 * order, with the centre on the grid. Null when none is left there.
 */
export function findFreeTablePosition(
  shape: TableShape,
  seatCount: number,
  width: number,
  height: number,
  existing: FloorTable[],
  area: PlanArea
): { x: number; y: number } | null {
  const table = { shape, seatCount, width, height, rotation: 0 }
  // Holds the outline whole, so a centre this far in keeps it on screen.
  const footprint = getTableFootprint(shape, seatCount, width, height)
  const halfW = footprint.width / 2
  const halfH = footprint.height / 2
  const others = existing.map(other =>
    getTableOutline(other, {
      x: other.x + other.width / 2,
      y: other.y + other.height / 2
    })
  )

  const firstX = Math.ceil((area.left + halfW) / GRID_SIZE) * GRID_SIZE
  const firstY = Math.ceil((area.top + halfH) / GRID_SIZE) * GRID_SIZE
  for (let y = firstY; y + halfH <= area.bottom; y += GRID_SIZE) {
    for (let x = firstX; x + halfW <= area.right; x += GRID_SIZE) {
      const outline = getTableOutline(table, { x, y })
      if (!others.some(other => outlinesOverlap(outline, other)))
        return { x: x - width / 2, y: y - height / 2 }
    }
  }
  return null
}
